import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query"

import { api } from "@/api"
import type { components } from "@/api/v1"
import { queryClient } from "@/lib/query-client"

export type PaginatedNotifications = components["schemas"]["PaginatedStaffNotificationList"]

export const notificationKeys = {
  all: ["notifications"] as const,
  list: () => [...notificationKeys.all, "list"] as const,
  unreadCount: () => [...notificationKeys.all, "unread-count"] as const,
}

export const useNotificationsInfiniteQuery = () =>
  useInfiniteQuery<PaginatedNotifications>({
    queryKey: notificationKeys.list(),
    queryFn: async ({ pageParam }) => {
      const { data } = await api.GET("/notifications/", {
        params: { query: { page: pageParam as number, pageSize: 15 } },
      })
      return data!
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: PaginatedNotifications) => {
      const next = lastPage.pagination.nextPage
      return next > 0 ? next : undefined
    },
  })

export const useUnreadCountQuery = () =>
  useQuery<{ unreadCount: number }>({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () =>
      api
        .GET("/notifications/unread-count/")
        .then(({ data }) => data!),
    refetchInterval: 30_000,
  })

export const useMarkNotificationReadMutation = () =>
  useMutation({
    mutationFn: (id: number) =>
      api.PATCH("/notifications/{id}/read/", { params: { path: { id } } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })

export const useMarkAllReadMutation = () =>
  useMutation({
    mutationFn: () => api.POST("/notifications/mark-all-read/"),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
